export const SHOPIFY_API_VERSION = import.meta.env.VITE_APP_SHOPIFY_API_VERSION || '2025-10';

export interface ShopifyGraphQLOptions {
    version?: string;
}

/**
 * Swaps the version segment of a gateway proxy URL, whose last path segment is the API version
 * (see `getEnvShopifyGraphQLGatewayConfig`). Returns the URL unchanged when no override is given.
 */
export function gatewayUrlWithApiVersion(url: string, version?: string): string {
    return version ? url.replace(/[^/]+\/?$/, version) : url;
}

export type OperationVariables = Record<string, unknown>;

export interface GraphQLResponse<T> {
    data?: T;
    errors?: Array<{ message: string; locations?: Array<{ line: number; column: number }> }>;
}

export class ShopifyGraphQLError extends Error {
    constructor(
        message: string,
        public errors?: GraphQLResponse<unknown>['errors'],
        public isRateLimited?: boolean
    ) {
        super(message);
        this.name = 'ShopifyGraphQLError';
    }
}

// Shopify connection types (Relay pattern)
export interface Edge<T> {
    node: T;
    cursor?: string;
}

export interface Connection<T> {
    edges: Edge<T>[];
    pageInfo?: {
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        startCursor?: string;
        endCursor?: string;
    };
}

function isConnection(value: unknown): value is Connection<unknown> {
    return (
        value !== null &&
        typeof value === 'object' &&
        'edges' in value &&
        Array.isArray((value as Connection<unknown>).edges)
    );
}

/** Recursively converts all nested edges/nodes patterns to arrays, preserving pageInfo */
export function flattenEdges<T>(obj: T): T {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(flattenEdges) as T;

    const result: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
        if (isConnection(value)) {
            const connection = value as Connection<unknown>;
            // Convert connection to object with nodes array and pageInfo
            result[key] = {
                nodes: connection.edges.map((edge) => flattenEdges(edge.node)),
                pageInfo: connection.pageInfo,
            };
        } else if (typeof value === 'object' && value !== null) {
            result[key] = flattenEdges(value);
        } else {
            result[key] = value;
        }
    }

    return result as T;
}

/** Extracts nodes from a Shopify connection and recursively flattens nested edges */
export function extractNodes<T>(connection: Connection<T> | null | undefined): T[] {
    if (!connection?.edges) return [];

    return connection.edges.map((edge) => {
        const node = edge.node;
        if (node && typeof node === 'object') {
            return flattenEdges(node) as T;
        }
        return node;
    });
}
